import React, { useState, useMemo } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Box,
  Text,
  Select,
  NumberInput,
  useMantineTheme,
  Paper
} from '@mantine/core';
import { IconPlus, IconCheck, IconX, IconTableExport } from '@tabler/icons-react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';

const PartSearchDialog = ({ opened, onClose, onPartSelect }) => {
  const theme = useMantineTheme();
  const [searchOn, setSearchOn] = useState('P/N');
  const [startWith, setStartWith] = useState('');
  const [mfg, setMfg] = useState('All');
  const [category, setCategory] = useState('All');
  const [pnType, setPnType] = useState('All');
  const [eligibility, setEligibility] = useState('All');
  const [maxResults, setMaxResults] = useState(50);
  const [selectedPart, setSelectedPart] = useState(null);

  // Sample parts data
  const parts = [
    {
      partnumber: '#10CS',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#10CSW.SSSH',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#10VFW.WHITE',
      subP: '1',
      rev: '',
      description: 'FIBER WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#1AA KIT',
      subP: '1',
      rev: '',
      description: '#1AA KIT W/ BOT1',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#1AP KIT',
      subP: '1',
      rev: '',
      description: '#1AP KIT',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#5CSW.SSSH',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#6CUPSS',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#6VFW.WHITE',
      subP: '1',
      rev: '',
      description: 'FIBER WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#715B32',
      subP: '1',
      rev: '',
      description: 'PISTON, HEAD',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#8CSW.SSSH',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#8CUPSS',
      subP: '1',
      rev: '',
      description: 'WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    },
    {
      partnumber: '#8VFW.WHITE',
      subP: '1',
      rev: '',
      description: 'FIBER WASHER',
      mfg: '',
      cage: '',
      mfgCode: '',
      mfgName: '',
      rec: ''
    }
  ];

  const handleRowClick = (part) => {
    setSelectedPart(part);
  };

  const handleOkClick = () => {
    if (selectedPart && onPartSelect) {
      onPartSelect(selectedPart);
    }
    onClose();
  };

  const searchOnOptions = ['P/N', 'Loc', 'X-Ref', 'Ser#', 'BOM', 'Cap', 'Ext', 'Components'];
  const filterOptions = ['All'];

  const columns = useMemo(() => [
    {
      accessorKey: 'partnumber',
      header: 'Partnumber',
      size: 140,
    },
    {
      accessorKey: 'subP',
      header: 'SubP',
      size: 60,
    },
    {
      accessorKey: 'rev',
      header: 'Rev',
      size: 60,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
    },
    {
      accessorKey: 'mfg',
      header: 'MFG',
      size: 80,
    },
    {
      accessorKey: 'cage',
      header: 'CAGE',
      size: 80,
    },
    {
      accessorKey: 'mfgCode',
      header: 'MFG',
      size: 80,
    },
    {
      accessorKey: 'mfgName',
      header: 'MFG Name',
      size: 120,
    },
    {
      accessorKey: 'rec',
      header: 'Rec',
      size: 60,
    },
  ], []);

  const table = useMantineReactTable({
    columns,
    data: parts,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    getRowId: (row) => row.partnumber,
    mantineTableProps: {
      highlightOnHover: true,
      withBorder: true,
      striped: true,
      fontSize: 'xs',
      style: { fontSize: '11px' }
    },
    mantineTableHeadProps: {
      style: { 
        fontSize: '11px',
       }
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => handleRowClick(row.original),
      style: {
        cursor: 'pointer',
        backgroundColor: selectedPart?.partnumber === row.original.partnumber 
          ? theme.colorScheme === 'dark' 
            ? theme.colors.blue[9] 
            : theme.colors.blue[1]
          : undefined,
      },
    }),
    mantineTableBodyCellProps: {
      style: { padding: '4px 8px', fontSize: '11px' }
    },
    initialState: {
      density: 'xs',
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Partnumber"
      size="95%"
      centered
      styles={{
        title: {
          fontSize: '14px',
          fontWeight: 'normal'
        },
        header: {
           borderBottom: '1px solid #ccc'
        },
        body: {
          padding: '8px'
        }
      }}
    >
      <Stack spacing="xs">
        {/* Search Controls */}
        <Paper p="sm" withBorder  >
          <Group spacing="md" align="flex-end">
            {/* Left side - Search On tabs and Start With */}
            <Box style={{ flex: 1 }}>
              <Group spacing="xs" mb="xs">
                <Text size="sm" weight={500}>Search On :</Text>
                <Group spacing={2}>
                  {searchOnOptions.map((option) => (
                    <Button
                      key={option}
                      size="xs"
                      variant={searchOn === option ? "filled" : "default"}
                      onClick={() => setSearchOn(option)}
                      styles={{
                        root: {
                          fontSize: '10px',
                          height: '22px',
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          backgroundColor: searchOn === option ? '#4A90E2' : undefined
                        }
                      }}
                    >
                      {option}
                    </Button>
                  ))}
                </Group>
              </Group>
              
              <Group spacing="xs" align="flex-end">
                <Box>
                  <Text size="sm" weight={500} mb={4}>Start With :</Text>
                  <TextInput
                    value={startWith}
                    onChange={(e) => setStartWith(e.target.value)}
                    size="xs"
                    style={{ width: '300px' }}
                    styles={{
                      input: {
                        fontSize: '11px',
                        height: '24px'
                      }
                    }}
                    rightSection={
                      <Button size="xs" variant="subtle" style={{ fontSize: '10px' }}>
                        •••
                      </Button>
                    }
                  />
                </Box>
              </Group>
            </Box>

            {/* Right side - Filters */}
            <Box>
              <Stack spacing="xs">
                <Group spacing="md">
                  <Group spacing="xs" align="flex-end">
                    <Text size="sm" weight={500}>MFG :</Text>
                    <Select
                      value={mfg}
                      onChange={setMfg}
                      data={filterOptions}
                      size="xs"
                      style={{ width: '80px' }}
                      styles={{ input: { fontSize: '11px', height: '20px' } }}
                    />
                    <Button size="xs" variant="filled" style={{ fontSize: '10px', height: '20px' }}>
                      All
                    </Button>
                  </Group>
                </Group>

                <Group spacing="md">
                  <Group spacing="xs" align="flex-end">
                    <Text size="sm" weight={500}>Category :</Text>
                    <Select
                      value={category}
                      onChange={setCategory}
                      data={filterOptions}
                      size="xs"
                      style={{ width: '80px' }}
                      styles={{ input: { fontSize: '11px', height: '20px' } }}
                    />
                    <Button size="xs" variant="filled" style={{ fontSize: '10px', height: '20px' }}>
                      All
                    </Button>
                  </Group>
                </Group>

                <Group spacing="md">
                  <Group spacing="xs" align="flex-end">
                    <Text size="sm" weight={500}>P/N Type :</Text>
                    <Select
                      value={pnType}
                      onChange={setPnType}
                      data={filterOptions}
                      size="xs"
                      style={{ width: '80px' }}
                      styles={{ input: { fontSize: '11px', height: '20px' } }}
                    />
                    <Button size="xs" variant="filled" style={{ fontSize: '10px', height: '20px' }}>
                      All
                    </Button>
                  </Group>
                </Group>

                <Group spacing="md">
                  <Group spacing="xs" align="flex-end">
                    <Text size="sm" weight={500}>Eligibility :</Text>
                    <Select
                      value={eligibility}
                      onChange={setEligibility}
                      data={filterOptions}
                      size="xs"
                      style={{ width: '80px' }}
                      styles={{ input: { fontSize: '11px', height: '20px' } }}
                    />
                    <Button size="xs" variant="filled" style={{ fontSize: '10px', height: '20px' }}>
                      All
                    </Button>
                  </Group>
                </Group>

                <Group spacing="xs" align="flex-end">
                  <Text size="sm" weight={500}>Max Search Result Lines :</Text>
                  <NumberInput
                    value={maxResults}
                    onChange={setMaxResults}
                    size="xs"
                    style={{ width: '60px' }}
                    styles={{ input: { fontSize: '11px', height: '20px', textAlign: 'right' } }}
                  />
                </Group>
              </Stack>
            </Box>
          </Group>
        </Paper>

        {/* Grouping hint */}
        <Text size="xs" color="dimmed" style={{ fontStyle: 'italic' }}>
          Drag a column header here to group by that column
        </Text>

        {/* Table */}
        <Box style={{ height: '400px' }}>
          <MantineReactTable table={table} />
        </Box>

        {/* Bottom buttons */}
        <Group position="apart" mt="md">
          <Group spacing="xs">
            <Button 
              size="sm" 
              leftIcon={<IconPlus size={16} />}
              variant="default"
              styles={{
                root: {
                  fontSize: '11px',
                  height: '28px'
                }
              }}
            >
              Add
            </Button>
            <Button 
              size="sm"
              leftIcon={<IconTableExport size={16} />}
              variant="default"
              styles={{
                root: {
                  fontSize: '11px',
                  height: '28px'
                }
              }}
            >
            </Button>
            <Button 
              size="sm"
              variant="default"
              styles={{
                root: {
                  fontSize: '11px',
                  height: '28px'
                }
              }}
            >
              🔧
            </Button>
          </Group>
          
          <Group spacing="xs">
            <Button 
              size="sm"
              leftIcon={<IconCheck size={16} />}
              onClick={handleOkClick}
              disabled={!selectedPart}
              styles={{
                root: {
                  fontSize: '11px',
                  height: '28px'
                }
              }}
            >
              OK
            </Button>
            <Button 
              size="sm"
              leftIcon={<IconX size={16} />}
              variant="default"
              onClick={onClose}
              styles={{
                root: {
                  fontSize: '11px',
                  height: '28px'
                }
              }}
            >
              Cancel
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default PartSearchDialog;